import React, { useCallback, useMemo, useState } from 'react';

import { BugIcon } from '@primer/octicons-react';
import { StyledOcticon } from '@primer/react';

import CardView from '../../../common/CardView/CardView';
import ErrorSlate from '../../../ErrorSlate/ErrorSlate';
import LoadingSpinner from '../../../common/LoadingSpinner/LoadingSpinner';
import PaginationRow from '../../../common/PaginationRow/PaginationRow';
import SearchInput from '../../../common/TextInputs/SearchInput/SearchInput';

import FilterCountButton from '../../FilterCountButton';
import ModelIcon from '../../../common/icons/ModelIcon';

import ModelAdminTable from '../ModelAdminTable/ModelAdminTable';
import ModelTableFilterModal from '../ModelTableFilterModal/ModelTableFilterModal';

import AdminModelHistoryModal from '../AdminModelHistoryModal/AdminModelHistoryModal';
import AdminModelRemovalModal from '../AdminModelRemovalModal/AdminModelRemovalModal';
import AdminModelVersionRemovalModal from '../AdminModelVersionRemovalModal/AdminModelVersionRemovalModal';

import removeModel from '../../../../utils/api_functions/removeModel';
import removeModelVersions from '../../../../utils/api_functions/removeModelVersions';

import useModelList from '../useModelList/useModelList';
import useWorkspaceContext from '../../../workspaces/WorkspaceContext/WorkspaceContext';
import classNames from 'classnames';

const MODEL_ADMIN_MODAL = {
  DELETE_MODEL: 'delete_model',
  DELETE_VERSIONS_CONFIRM: 'delete_model_versions',
  FILTER_MODELS: 'filter_models',
  MODEL_HISTORY: 'model_history',
}

const BugGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={BugIcon}
    size={64}
  />
)

const NoModelsGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={ModelIcon}
    size={16}
  />
)

const countActiveModelFilters = (filterObj = {}) => {
  let filterCount = 0;
  if (filterObj.workspaces?.length) {
    filterCount += 1;
  }

  return filterCount;
}

export const ModelAdminTab = ({
  error = null,
  loading = false,
  models = [],
  workspaces = [],
  totalModelCount = 0,
  searchQuery = '',
  filterObj = { workspaces: [] },
  setSearchQuery = () => {},
  setFilterObj = () => {},
}) => {
  const [selectedPage, setSelectedPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(25);

  const [showModal, setShowModal] = useState(null);
  // Either ID or Pipeline object as needed
  const [focusedModel, setFocusedModel] = useState(null)
  const [modalError, setModalError] = useState(null);

  const {
    setCurrentWorkspaceId,
  } = useWorkspaceContext();

  const handleSearchInputChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery])

  const handleModelRemoval = (modelId, workspaceId) => {
    setModalError(null);
    removeModel(modelId, workspaceId).then((res) => {
      // 409 errors don't resolve as expected - handle them here
      if (res?.code && res.code >= 400) {
        if (res.code === 409) {
          setModalError('Cannot delete a model that is actively deployed. Please undeploy the pipeline this model is a part of before attempting to delete it.');
        } else {
          const message = res?.msg || 'An unexpected error has occurred while trying to remove the selected model. Please try again in a few minutes.';
          setModalError(message);
        }
      } else {
        setShowModal(null);
        setFocusedModel(null);
        setModalError(null);
      }
    }).catch((e) => {
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to remove the selected model. Please try again in a few minutes.')
      }
    })
  }

  const handleMultiVersionRemoval = (versions) => {
    setModalError(null);
    removeModelVersions(versions).then((res) => {
      // 409 errors don't resolve as expected - handle them here
      if (res?.code && res.code >= 400) {
        if (res.code === 409) {
          setModalError('Cannot delete a model that is actively deployed. Please undeploy the pipeline this model is a part of before attempting to delete it.');
        } else {
          const message = res?.msg || 'An unexpected error has occurred while trying to remove the selected model(s). Please try again in a few minutes.';
          setModalError(message);
        }
      } else {
        setShowModal(null);
        setFocusedModel(null);
        setModalError(null);
      }
    }).catch((e) => {
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to remove the selected model(s). Please try again in a few minutes.')
      }
    })
  }

  const onShowModelHistory = (row) => {
    setShowModal(MODEL_ADMIN_MODAL.MODEL_HISTORY)
    setFocusedModel(row);
    setModalError(null);
  }

  const onShowModelRemovalModal = (row) => {
    setShowModal(MODEL_ADMIN_MODAL.DELETE_MODEL)
    setFocusedModel(row);
    setModalError(null);
  }

  const onVersionsSelectedForRemoval = (versions) => {
    setShowModal(MODEL_ADMIN_MODAL.DELETE_VERSIONS_CONFIRM)
    setFocusedModel(versions);
    setModalError(null);
  }

  const onModalToggle = (row) => {
    if (!row) {
      setShowModal(null);
      setFocusedModel(null);
      setModalError(null);
    }
  }

  // const onModalClose = () => {
  //   setShowModal(null);
  //   setFocusedPipeline(null);
  //   setModalError(null);
  // }

  const onFilterConfirmed = (filter) => {
    setFilterObj(filter);
    setShowModal(null);
    setModalError(null);
  }

  const onFilterClear = () => {
    setFilterObj({
      workspaces: [],
    })
    setShowModal(null);
    setModalError(null);
  }

  // TODO - How do we handle the case where there are no more versions?
  const handleModelClick = useCallback(({ original }) => {
    // Product behavior: Should change to the model's workspace. https://wallarooai.slack.com/archives/D04D8SN29FW/p1716477004678179
    setCurrentWorkspaceId(original?.workspace?.workspace_id);
    const route = `workspaces/${original?.workspace?.workspace_id}/models/${original?.name}/versions/${original?.latestVersion?.file_info?.version}`;
    window.open(`${window.location.origin}/${route}`, '_blank')
  }, [setCurrentWorkspaceId])

  const formatResultsCount = useCallback(val => `${val}/${totalModelCount} results`, [totalModelCount]);

  const errorText = error ? 'An unexpected error occurred while trying to load the model list.' : 'No models found.';

  // Hides the CTA section if there are simply no users to display
  const errorProps = error ? {} : { CTAInfo: null }

  return (
    <div>
      <div className="flex items-center justify-start mb-2">
        <SearchInput className="mx-2" value={searchQuery} onChange={handleSearchInputChange} block />
        <FilterCountButton activeFilters={countActiveModelFilters(filterObj)} onClick={() => setShowModal(MODEL_ADMIN_MODAL.FILTER_MODELS)}>Filter</FilterCountButton>
      </div>
      <div className="relative">
        {
          (loading) && (
            <LoadingSpinner
              animationKey="model-admin-table-loading-spinner"
              loadingText="Loading..."
              useOverlay
            />
          )
        }
        <div className={classNames('border shadow', {
          'hidden': !models?.length || error,
        })}>
          <ModelAdminTable
            models={models}
            onModelClick={handleModelClick}
            removeModel={onShowModelRemovalModal}
            viewModelHistory={onShowModelHistory}
            selectedPage={selectedPage}
            resultsPerPage={resultsPerPage}
          />
        </div>
        {
          (!models?.length || error) && (
            <CardView className="p-4" disableFullHeight>
              <ErrorSlate
                errorText={errorText}
                subText={null}
                Graphic={error ? BugGraphic : NoModelsGraphic}
                {...errorProps}
              />
            </CardView>
          )
        }
      </div>
      <PaginationRow
        className="p-4"
        numberOfResults={models?.length}
        selectedPage={selectedPage}
        resultsPerPage={resultsPerPage}
        setSelectedPage={setSelectedPage}
        setResultsPerPage={setResultsPerPage}
        formatResultsCount={formatResultsCount}
      />
      {
        showModal === MODEL_ADMIN_MODAL.FILTER_MODELS && (
          <ModelTableFilterModal
            filter={filterObj}
            workspaces={workspaces}
            showDialog={showModal === MODEL_ADMIN_MODAL.FILTER_MODELS}
            onDialogToggle={onModalToggle}
            onFilterConfirm={onFilterConfirmed}
            onClear={onFilterClear}
          />
        )
      }
      { 
        showModal === MODEL_ADMIN_MODAL.DELETE_MODEL && ( 
          <AdminModelRemovalModal 
            error={modalError} 
            model={focusedModel} 
            showDialog={showModal === MODEL_ADMIN_MODAL.DELETE_MODEL} 
            onModelRemoved={handleModelRemoval} 
            onDialogToggle={onModalToggle} 
          /> 
        ) 
      }
      {
        showModal === MODEL_ADMIN_MODAL.DELETE_VERSIONS_CONFIRM && (
          <AdminModelVersionRemovalModal
            error={modalError}
            versions={focusedModel}
            showDialog={showModal === MODEL_ADMIN_MODAL.DELETE_VERSIONS_CONFIRM}
            onVersionsRemoved={handleMultiVersionRemoval}
            onDialogToggle={onModalToggle}
          />
        )
      }
      { 
        showModal === MODEL_ADMIN_MODAL.MODEL_HISTORY && ( 
          <AdminModelHistoryModal 
            error={modalError} 
            row={focusedModel} 
            showDialog={showModal === MODEL_ADMIN_MODAL.MODEL_HISTORY} 
            onDialogToggle={onModalToggle} 
            onVersionsSelected={onVersionsSelectedForRemoval}
          /> 
        ) 
      } 
    </div>
  )
}

const handleSearch = (models, searchQuery, filterObj) => models?.filter((model) => {
  const workspace_filter = filterObj.workspaces.length === 0 || filterObj.workspaces.some(w => w.id === model?.workspace?.id)
  const searchTerm = searchQuery.toLowerCase();

  const versions = model?.model_versions;
  const firstVersion = versions?.length > 0 ? versions[0]?.model_version : null;

  const modelName = model?.model?.name?.toLowerCase() || '';
  const workspaceName = model?.workspace?.name?.toLowerCase() || '';
  const workspaceOwner = model?.workspace?.created_by?.toLowerCase() || '';
  const modelSha = firstVersion?.file_info?.sha?.toLowerCase() || '';
  const updatedBy = firstVersion?.created_by?.toLowerCase() || '';

  const search_filter = modelName.includes(searchTerm)
    || workspaceName.includes(searchTerm)
    || workspaceOwner.includes(searchTerm)
    || modelSha.includes(searchTerm)
    || updatedBy.includes(searchTerm);

  return workspace_filter && search_filter;
})

const ContextualModelAdminTab = () => {
  const [filterObj, setFilterObj] = useState({
    workspaces: [],
  })

  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: models,
    isLoading,
    error,
  } = useModelList();

  const {
    workspaces,
  } = useWorkspaceContext();

  const filteredModels = useMemo(() => handleSearch(models, searchQuery, filterObj), [filterObj, searchQuery, models]);
  return (
    <ModelAdminTab
      error={error}
      loading={isLoading}
      models={filteredModels}
      workspaces={workspaces}
      totalModelCount={models?.length || 0}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterObj={filterObj}
      setFilterObj={setFilterObj}
    />
  )
}

export default ContextualModelAdminTab;
